import { IOnboardingAddressRequest } from '@/@types/Onboarding';
import { toast } from '@/hooks/use-toast';
import { getCep } from '@/services/cep';
import { OnboardingService } from '@/services/onboarding.service';
import { ProfileService } from '@/services/profile.service';
import { getDefaultCountryAlpha2ByLanguage } from '@/utils/browser-locale-defaults';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export const useOnboardingAdressStep = ({
  onClose,
  isOpen,
  mode,
}: {
  onClose: () => void;
  isOpen: boolean;
  mode?: 'edit' | 'create';
}) => {
  const { t } = useTranslation(['common', 'onboarding']);
  const browserDefaultCountry = getDefaultCountryAlpha2ByLanguage();
  const displayDefaultCountry = mode === 'edit' ? 'BR' : browserDefaultCountry;

  const [cep, setCep] = useState('');
  const [cepComplete, setCepComplete] = useState(false);
  const [cepHelperText, setCepHelperText] = useState('');
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  const CreateAddressSchema = z
    .object({
      country: z.string().min(2, t('required_field')),
      postal_code: z.string().min(3, t('required_field')),
      state: z.string().min(2, t('required_field')),
      city: z.string().min(2, t('required_field')),
      street: z.string().min(2, t('required_field')),
      complement: z.string().optional(),
      district: z.string().optional(),
      number: z
        .string()
        .min(1, t('required_field'))
        .refine(
          (val) => {
            const num = Number(val);
            return !isNaN(num) && num >= 0;
          },
          {
            message: t('required_field'),
          },
        ),
    })
    .superRefine((data, ctx) => {
      const country = (data.country || 'BR').toUpperCase();
      const postal = (data.postal_code || '').trim();

      if (country === 'BR') {
        const onlyDigits = postal.replace(/\D/g, '');
        if (onlyDigits.length !== 8) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['postal_code'],
            message: t('invalid_cep'),
          });
        }

        const district = (data.district || '').trim();
        if (!district || district.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['district'],
            message: t('required_field'),
          });
        }
      } else {
        // Postal code global: aceita alfanumérico; validação básica de tamanho.
        if (postal.length < 3) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['postal_code'],
            message: t('required_field'),
          });
        }
      }
    });

  const { data: userAddress, isPending: isLoadingUserAddress } = useQuery({
    queryKey: ['address'],
    queryFn: async () => {
      const response = await OnboardingService.getAddress();
      return response.data.data?.[0];
    },
    staleTime: 1000 * 60 * 60 * 24,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    getValues,
    clearErrors,
    watch,
  } = useForm<IOnboardingAddressRequest>({
    shouldUnregister: false,
    resolver: zodResolver(CreateAddressSchema),
    defaultValues: {
      country: displayDefaultCountry,
      state: '',
      city: '',
      district: '',
      street: '',
      complement: '',
      number: '',
      postal_code: '',
    },
  });

  useEffect(() => {
    if (userAddress) {
      setValue('state', userAddress.state || '');
      setValue('city', userAddress.city || '');
      setValue('district', userAddress.district || '');
      setValue('street', userAddress.street || '');
      setValue('complement', userAddress.complement || '');
      setValue('number', userAddress.number || '');
      setValue('postal_code', userAddress.postal_code || '');
      setValue('country', (userAddress.country || displayDefaultCountry).toUpperCase());

      if (userAddress.postal_code) {
        setCepComplete(true);
        setCepHelperText('');
      }
    } else {
      setValue('state', '');
      setValue('city', '');
      setValue('district', '');
      setValue('street', '');
      setValue('complement', '');
      setValue('number', '');
      setValue('postal_code', '');
      setValue('country', displayDefaultCountry);
      setCepComplete(false);
      setCepHelperText('');
    }
  }, [userAddress, setValue, isOpen, displayDefaultCountry]);

  const queryClient = useQueryClient();
  const defaultValues = getValues();

  const { mutate, isPending: isSendingAddress } = useMutation({
    mutationFn: async (data: IOnboardingAddressRequest) =>
      mode === 'edit'
        ? await ProfileService.updateAddress(data)
        : await OnboardingService.address(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
      await queryClient.invalidateQueries({ queryKey: ['address'] });
      toast({
        description: t('data_saved'),
        variant: 'success',
      });
      onClose();
    },
    onError: (err: any) => {
      Object.keys(err.response.data.errors).forEach((key: any) => {
        setError(key, {
          type: 'manual',
          message: err.response.data.errors[key],
        });
      });
    },
  });

  const handlerCep = async (value: string) => {
    try {
      const currentCountry = (getValues('country') || 'BR').toUpperCase();
      if (currentCountry !== 'BR') return;

      setIsLoadingCep(true);
      setCepComplete(false);
      setCepHelperText('');

      const data = await getCep(value);

      if (!data) {
        throw new Error(t('cep_not_found'));
      }

      setCepComplete(true);
      setCepHelperText('');

      setValue('state', data.state);
      setValue('city', data.city);
      setValue('district', data.neighborhood);
      setValue('street', data.street);
      setValue('postal_code', value);
      setValue('country', 'BR');

      clearErrors();
    } catch (err) {
      setCepComplete(false);
      setCepHelperText(t('cep_not_found'));

      setValue('state', '');
      setValue('city', '');
      setValue('district', '');
      setValue('street', '');
      setValue('postal_code', value);
      setValue('country', 'BR');

      if (userAddress) {
        if (!getValues('number')) {
          setValue('number', userAddress.number || '');
        }
        if (!getValues('complement')) {
          setValue('complement', userAddress.complement || '');
        }
      }
    } finally {
      setIsLoadingCep(false);
    }
  };

  const onSubmit = async (data: IOnboardingAddressRequest) => {
    try {
      const country = (data.country || 'BR').toUpperCase();

      if (country === 'BR') {
        const onlyDigits = (data.postal_code || '').replace(/\D/g, '');
        if (!onlyDigits || onlyDigits.length !== 8) {
          setCepHelperText(t('invalid_cep'));
          return;
        }

        if (!cepComplete && !userAddress?.postal_code) {
          await handlerCep(onlyDigits);
        }
      } else {
        setCepHelperText('');
      }

      const requiredFields =
        country === 'BR'
          ? ['state', 'city', 'street', 'district', 'number']
          : ['state', 'city', 'street', 'number'];
      const missingFields = requiredFields.filter(
        (field) => !data[field as keyof IOnboardingAddressRequest],
      );

      if (missingFields.length > 0) {
        missingFields.forEach((field) => {
          setError(field as any, {
            type: 'manual',
            message: t('required_field'),
          });
        });
        return;
      }

      mutate(data);
    } catch (error) {
      // console.error('Erro ao processar formulário:', error);
      toast({
        description: t('data_submission_error'),
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      clearErrors();
    }
  }, [isOpen]);

  return {
    cep,
    setCep,
    register,
    handleSubmit,
    errors,
    defaultValues,
    onSubmit,
    handlerCep,
    isSendingAddress,
    isLoadingUserAddress,
    cepHelperText,
    setValue,
    isLoadingCep,
    watch,
  };
};
